import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

export type PushCopy = {
    language: string;
    title: string;
    body: string;
};

/** What a campaign did. cancelledBecauseOrdered is the campaign working, not failing. */
export type PushCampaignStats = {
    queued: number;
    sent: number;
    cancelledBecauseOrdered: number;
    cancelledOther: number;
    attributedOrders: number;
    attributedRevenue: number;
    /** Sent by email instead of push (customers without the app, e.g. website). Absent on an older backend. */
    emailed?: number;
    /** Opened by tapping the notification (count of tapped_at). Absent on an older backend. */
    tapped?: number;
};

export type PushCampaign = {
    id: number;
    campaignKey: string;
    name: string;
    triggerType: string;
    isActive: boolean;
    sequenceStep: number;
    delayHours: number;
    /** Minute-scale nudges (stuck payment, abandoned cart). Absent on an older backend. */
    delayMinutes?: number;
    /** Minutes past local midnight, comma separated. 720 = 12:00. */
    sendWindows: string;
    expireAfterHours: number;
    deepLinkTarget: string;
    /** The venue this campaign advertises; null = either half of the pair / chosen per send. */
    targetLocationId?: number | null;
    copy: PushCopy[];
    stats: PushCampaignStats;
};

export type UpdatePushCampaign = {
    isActive?: boolean;
    delayHours?: number;
    sendWindows?: string;
    expireAfterHours?: number;
    copy?: PushCopy[];
};

/** A venue that may be half of the cross-venue pair. */
export type VenueOption = {
    id: number;
    name: string;
    city: string | null;
};

export type VenuePair = {
    firstLocationId: number | null;
    secondLocationId: number | null;
    options: VenueOption[];
};

/** "today" = seen in the last 24 h. */
export type WhereWindow = 'today' | '7d' | 'all';

/** One country's share of the phones seen in the window (`GET /admin/devices/where`). */
export type WhereCountry = {
    /** ISO 3166-1 alpha-2, e.g. "CY". */
    code: string;
    name: string;
    devices: number;
    customers: number;
};

/**
 * One phone seen in the window. Country is derived from the phone's time zone;
 * nothing finer than the country is stored.
 *
 * `source` says where the time zone came from: DEVICE is the 1.11+ app reporting
 * on open and on its twice-daily silent check; CUSTOMER_PROFILE is the zone an
 * older app (1.10 and below) sent at its last open, which goes stale until the
 * phone updates.
 */
export type WherePerson = {
    deviceId: number;
    customerId: number | null;
    name: string | null;
    phone: string | null;
    country: string | null;
    timeZone: string | null;
    lastSeenAt: string | null;
    source: 'DEVICE' | 'CUSTOMER_PROFILE';
    lastOrderAt: string | null;
    lastOrderCity: string | null;
    isLocal: boolean | null;
    tripArrivedAt: string | null;
};

export type DevicesWhere = {
    generatedAt: string;
    window: WhereWindow;
    countries: WhereCountry[];
    people: WherePerson[];
};

/** One stay of a phone in a country (`GET /admin/devices/stays`). leftAt null = there now. */
export type Stay = {
    id: number;
    deviceId: number;
    customerId: number | null;
    /** ISO 3166-1 alpha-2, or null when the zone could not be placed. */
    country: string | null;
    countryName: string | null;
    timeZone: string;
    arrivedAt: string;
    leftAt: string | null;
    source: string | null;
    nights: number;
};

export type QueueStatus = 'pending' | 'sent' | 'cancelled' | 'all';

/** One scheduled push in the queue report (`GET /admin/push-queue`). */
export type QueueRow = {
    id: number;
    campaignId: number;
    campaignKey: string;
    campaignName: string;
    triggerType: string | null;
    status: 'PENDING' | 'SENT' | 'CANCELLED';
    dueAt: string;
    sentAt: string | null;
    cancelledAt: string | null;
    cancelReason: string | null;
    tappedAt: string | null;
    tapAction: string | null;
    channel: string;
    customerId: number | null;
    customerName: string | null;
    phone: string | null;
    deviceId: number | null;
    country: string | null;
    restaurant: string | null;
    cartItems: number | null;
    triggerOrderId: number | null;
    attributedOrderId: number | null;
    creditGranted: boolean;
    tripId: number | null;
};

export type PushQueue = {
    generatedAt: string;
    status: QueueStatus;
    days: number;
    pending: number;
    sent: number;
    cancelled: number;
    rows: QueueRow[];
};

export default class PushService {
    async list(): Promise<PushCampaign[]> {
        try {
            const response = await axiosInstance.get('/push-campaigns');
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-campaigns-list' } });
            throw e;
        }
    }

    async update(id: number, patch: UpdatePushCampaign): Promise<PushCampaign> {
        try {
            const response = await axiosInstance.patch(`/push-campaigns/${id}`, patch);
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-campaigns-update' } });
            throw e;
        }
    }

    async getVenuePair(): Promise<VenuePair> {
        try {
            const response = await axiosInstance.get('/push-campaigns/venue-pair');
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-venue-pair-get' } });
            throw e;
        }
    }

    /** Both null clears the pair, which stops every cross-venue nudge at source. */
    async setVenuePair(firstLocationId: number | null, secondLocationId: number | null): Promise<VenuePair> {
        try {
            const response = await axiosInstance.put('/push-campaigns/venue-pair', {
                firstLocationId,
                secondLocationId
            });
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-venue-pair-set' } });
            throw e;
        }
    }

    /**
     * Staging test override: send the "finishing soon" message for one trip-plan
     * item now, as if its attraction ended in 30 minutes. Admin only on the server.
     */
    async fireFinishingSoon(tripPlanItemId: number, ignoreCap: boolean, ignoreQuietHours = false): Promise<string> {
        try {
            const response = await axiosInstance.post('/push-campaigns/finishing-soon/fire', null, {
                params: { tripPlanItemId, ignoreCap, ignoreQuietHours }
            });
            const data = response.data;
            if (typeof data === 'string') return data;
            return data?.result ?? JSON.stringify(data);
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-finishing-soon-fire' } });
            throw e;
        }
    }

    /** Where the customers' phones are, by country. Admin only on the server. */
    /** Admin test: pretend a phone reported a time zone (Asia/Nicosia = landed, Asia/Jerusalem = left). */
    async simulateZone(deviceId: number, timeZone: string): Promise<{ deviceId: number; country: string | null }> {
        try {
            const response = await axiosInstance.post(`/admin/devices/${deviceId}/zone`, null, { params: { timeZone } });
            return response.data;
        } catch (error) {
            Sentry.captureException(error, { tags: { source: 'devices-simulate-zone' } });
            throw error;
        }
    }

    async where(window: WhereWindow): Promise<DevicesWhere> {
        try {
            const response = await axiosInstance.get('/admin/devices/where', { params: { window } });
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'devices-where' } });
            throw e;
        }
    }

    /** Fire this campaign at one phone now, using the real copy. */
    async sendTest(id: number, customerId: number, language?: string): Promise<string> {
        try {
            const response = await axiosInstance.post(`/push-campaigns/${id}/test`, { customerId, language });
            return response.data?.result ?? 'Sent.';
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-campaigns-test' } });
            throw e;
        }
    }
    /** The movement history of one phone, or of every phone a customer has used. Newest first. */
    async stays(q: { deviceId?: number; customerId?: number }): Promise<Stay[]> {
        try {
            const response = await axiosInstance.get('/admin/devices/stays', { params: q });
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'devices-stays' } });
            throw e;
        }
    }

    /** The push queue: pending (all of it), or what was sent / cancelled in the last `days`. */
    async queue(status: QueueStatus, days: number, campaignId?: number): Promise<PushQueue> {
        try {
            const response = await axiosInstance.get('/admin/push-queue', { params: { status, days, campaignId } });
            return response.data;
        } catch (e) {
            Sentry.captureException(e, { tags: { source: 'push-queue' } });
            throw e;
        }
    }
}
